// Conditional call-forwarding condition list + per-carrier dial codes.
// Kept in sync with SelectCarrier.js `categories`.

export const CONDITION_ALL = "all";
export const CONDITION_BUSY = "busy";
export const CONDITION_UNANSWERED = "unanswered";
export const CONDITION_UNREACHABLE = "unreachable";

// Order here drives the popup checkbox order AND the numbered step order.
export const FORWARDING_CONDITIONS = [
  { key: CONDITION_ALL, label: "All Calls (Unconditional)", short: "All Calls" },
  { key: CONDITION_BUSY, label: "When Busy", short: "When Busy" },
  { key: CONDITION_UNANSWERED, label: "When Unanswered", short: "When Unanswered" },
  { key: CONDITION_UNREACHABLE, label: "When Unreachable/Switched Off", short: "When Unreachable" },
];

// Every condition except "all" — these three together mean "all".
export const CONDITIONAL_KEYS = [CONDITION_BUSY, CONDITION_UNANSWERED, CONDITION_UNREACHABLE];

// MVNOs ride their parent network: Cricket/Metro/Boost/Mint/Consumer/Fi → GSM,
// Visible/Xfinity/Spectrum → CDMA. Canadian flankers follow their parent too.
// Kept in sync with `callForwardingConfig` in ForwardInstructions.js.
const GSM_CARRIERS = new Set([
  "AT&T",
  "T-Mobile US",
  "Cricket Wireless",
  "Metro by T-Mobile",
  "Boost Mobile",
  "Mint Mobile",
  "Consumer Cellular",
  "Google Fi",
  "Rogers",
  "Freedom Mobile",
  "Fido",
  "Chatr Mobile",

  // India — every network here is GSM and uses the same 3GPP MMI codes as the
  // table below. Without them getNetworkForCarrier() fell through to its cdma
  // default and handed an Indian user Verizon's `*72`, which their switch does
  // not answer to.
  "Jio",
  "Airtel",
  "Vi",
  "BSNL",
]);

// Unknown / empty carrier → cdma, matching the historic `*72{number}`
// fallback in ForwardInstructions so a stale carrier can't silently flip
// a user onto a different code family.
export const getNetworkForCarrier = (carrier) =>
  GSM_CARRIERS.has(carrier) ? "gsm" : "cdma";

/*
 * Number format is NOT the same on both networks, and getting it wrong makes
 * the code fail silently at the switch:
 *
 *   GSM  — MMI codes take the number in E.164 with the leading `+`
 *          (**21*+19495557890#). Standard and what we already ship.
 *   CDMA — Verizon documents "call *72 + the 10-digit number", area code
 *          required, country code NOT included. `*72+19495557890` is invalid.
 *          https://www.verizon.com/support/call-forwarding-faqs/
 *
 * voip_numbers are stored E.164 (VOIP_REGEX in agentController.js), so CDMA
 * codes have to be stripped down to the national 10 digits.
 */
export const formatNumberForNetwork = (number, network) => {
  const raw = String(number || "").trim();
  if (!raw || network !== "cdma") return raw;

  const digits = raw.replace(/\D/g, "");
  // NANP: drop the leading country code so the code gets exactly 10 digits.
  if (digits.length === 11 && digits.startsWith("1")) return digits.slice(1);
  return digits;
};

// GSM = 3GPP MMI codes. CDMA has no separate busy/unanswered/unreachable in
// Verizon's official wireless docs — `*72` is all calls, `*71` covers the
// calls you don't take (busy / no answer / unreachable), `*73` cancels both.
const CODE_TABLE = {
  gsm: {
    [CONDITION_ALL]: { activate: "**21*{number}#", deactivate: "##21#" },
    [CONDITION_BUSY]: { activate: "**67*{number}#", deactivate: "##67#" },
    [CONDITION_UNANSWERED]: { activate: "**61*{number}#", deactivate: "##61#" },
    [CONDITION_UNREACHABLE]: { activate: "**62*{number}#", deactivate: "##62#" },
  },
  cdma: {
    [CONDITION_ALL]: { activate: "*72{number}", deactivate: "*73" },
    [CONDITION_BUSY]: { activate: "*71{number}", deactivate: "*73" },
    [CONDITION_UNANSWERED]: { activate: "*71{number}", deactivate: "*73" },
    [CONDITION_UNREACHABLE]: { activate: "*71{number}", deactivate: "*73" },
  },
};

export const getConditionMeta = (key) =>
  FORWARDING_CONDITIONS.find((c) => c.key === key) || null;

/**
 * Collapse a raw checkbox selection into what we actually dial.
 *
 * "All Calls" is the ONLY thing that produces the single unconditional code.
 * Ticking busy + unanswered + unreachable individually is a deliberately
 * different choice — the user wants a code per condition, so all three are
 * kept and the screen lists three rows. (An earlier build folded three
 * conditionals into "all"; that hid the per-condition codes the user asked
 * for, so the fold was removed.)
 */
export const normalizeConditions = (conditions = []) => {
  const set = new Set((conditions || []).filter(Boolean));
  if (set.has(CONDITION_ALL)) return [CONDITION_ALL];
  return FORWARDING_CONDITIONS.map((c) => c.key).filter((k) => set.has(k));
};

// True once the tick-boxes add up to unconditional forwarding.
export const isUnconditional = (conditions = []) =>
  normalizeConditions(conditions)[0] === CONDITION_ALL;

/**
 * Build the ordered dial steps for a carrier + selection.
 * Conditions that resolve to the SAME code (the CDMA case) merge into one
 * step so the user never dials the same thing twice.
 *
 * → [{ key, conditions: ["busy","unanswered"], label, code, deactivateCode, order }]
 */
export const buildForwardingSteps = (carrier, number, conditions = []) => {
  const normalized = normalizeConditions(conditions);
  if (!normalized.length) return [];

  const network = getNetworkForCarrier(carrier);
  const table = CODE_TABLE[network] || CODE_TABLE.cdma;
  // CDMA wants 10 digits, GSM wants E.164 — see formatNumberForNetwork.
  const dialNumber = formatNumberForNetwork(number, network);

  const byCode = new Map();
  normalized.forEach((key) => {
    const entry = table[key];
    if (!entry) return;
    const code = dialNumber
      ? entry.activate.replace("{number}", dialNumber)
      : entry.activate;
    const existing = byCode.get(code);
    if (existing) {
      existing.conditions.push(key);
      return;
    }
    byCode.set(code, {
      key,
      conditions: [key],
      code,
      deactivateCode: entry.deactivate,
    });
  });

  return Array.from(byCode.values()).map((step, i) => ({
    ...step,
    label: step.conditions.map((k) => getConditionMeta(k)?.short || k).join(" · "),
    order: i + 1,
  }));
};

// Payload shape persisted to `call_forwarding_settings` — same object is
// mirrored into sessionStorage so a reload rebuilds the screen identically.
export const buildForwardingPayload = ({ carrier, number, conditions }) => {
  const normalized = normalizeConditions(conditions);
  const steps = buildForwardingSteps(carrier, number, normalized);
  return {
    carrier: carrier || "",
    network: getNetworkForCarrier(carrier),
    mode: isUnconditional(normalized) ? "unconditional" : "conditional",
    conditions: normalized,
    selectedConditions: (conditions || []).filter(Boolean),
    forwardTo: number || "",
    codes: steps.map((s) => ({
      condition: s.key,
      conditions: s.conditions,
      label: s.label,
      code: s.code,
      deactivateCode: s.deactivateCode,
      order: s.order,
    })),
  };
};

// sessionStorage bridge — carrier screen writes, forwarding screen reads.
export const CONDITIONS_SESSION_KEY = "forwardingConditions";

export const readStoredConditions = () => {
  try {
    const raw = sessionStorage.getItem(CONDITIONS_SESSION_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
  } catch {
    return [];
  }
};

export const writeStoredConditions = (conditions = []) => {
  try {
    sessionStorage.setItem(
      CONDITIONS_SESSION_KEY,
      JSON.stringify((conditions || []).filter(Boolean)),
    );
  } catch {
    /* private mode — screen falls back to unconditional */
  }
};

export const clearStoredConditions = () => {
  try {
    sessionStorage.removeItem(CONDITIONS_SESSION_KEY);
  } catch {
    /* no-op */
  }
};
